'use client';

/**
 * EntityHubTemplate — Reusable hub page layout for entity detail views.
 *
 * Provides: memoized KPI ribbon (no re-animation on tab change), tab bar, responsive grid.
 * Used by: Entity Hub, Borrower Profile, Guarantor Profile, Consumer Entity Hub.
 *
 * @see Issues #1196, #1197, #1199
 */

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import type { NavTab } from '@ops-matrix/shared-types';
import React, { useRef } from 'react';
import { PageTabBar } from '../PageTabBar';
import { StatCard } from '../StatCard';

// ============================================================================
// Types
// ============================================================================

export interface KpiCard {
  /** KPI label */
  title: string;
  /** KPI display value */
  value: string | number;
  /** Optional supporting text below the value */
  subtitle?: string;
  /** Accent color for the stat card */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /** Optional icon rendered inside the card */
  icon?: React.ReactNode;
}

export interface EntityHubTemplateProps {
  /** KPI cards shown above tabs */
  kpiCards: KpiCard[];
  /** Tab definitions */
  tabs: NavTab[];
  /** Currently active tab ID */
  activeTab: string;
  /** Called when tab changes */
  onTabChange: (tabId: string, path: string) => void;
  /** Tab content */
  children: React.ReactNode;
  /** Container max width (default: 'lg') */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false;
  /** Whether data is loading */
  loading?: boolean;
}

// ============================================================================
// KPI Ribbon (memoized — does not re-render / re-animate on tab change)
// ============================================================================

interface KpiRibbonProps {
  cards: KpiCard[];
  shouldAnimate: boolean;
}

const KpiRibbon = React.memo<KpiRibbonProps>(({ cards, shouldAnimate }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        {cards.map((card, index) => (
          <Grid key={card.title} size={{ xs: 6, sm: 4, md: 'grow' }}>
            <StatCard
              title={card.title}
              value={card.value}
              subtitle={card.subtitle}
              color={card.color ?? 'primary'}
              icon={card.icon}
              animate={shouldAnimate}
              animationDelay={shouldAnimate ? index * 0.08 : 0}
              hoverLift
              glowOnHover
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

KpiRibbon.displayName = 'KpiRibbon';

// ============================================================================
// KPI Ribbon Skeleton
// ============================================================================

function KpiRibbonSkeleton({ count = 4 }: { count?: number }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        {Array.from({ length: count }, (_, i) => (
          <Grid key={i} size={{ xs: 6, sm: 4, md: 'grow' }}>
            <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ============================================================================
// EntityHubTemplate
// ============================================================================

export const EntityHubTemplate: React.FC<EntityHubTemplateProps> = ({
  kpiCards,
  tabs,
  activeTab,
  onTabChange,
  children,
  maxWidth = 'lg',
  loading = false,
}) => {
  // Track whether the entrance animation has already played.
  // Once it fires on the first render it should never replay (e.g. on tab switch).
  const hasAnimatedRef = useRef(false);
  const shouldAnimate = !hasAnimatedRef.current;
  if (shouldAnimate) {
    hasAnimatedRef.current = true;
  }

  return (
    <Container maxWidth={maxWidth} sx={{ py: 3 }}>
      {/* KPI ribbon */}
      {loading ? (
        <KpiRibbonSkeleton count={kpiCards.length || 4} />
      ) : (
        <KpiRibbon cards={kpiCards} shouldAnimate={shouldAnimate} />
      )}

      {/* Tab bar */}
      <PageTabBar tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} sticky={false} embedded />

      {/* Tab content */}
      <Box sx={{ mt: 3 }}>{children}</Box>
    </Container>
  );
};

EntityHubTemplate.displayName = 'EntityHubTemplate';

export default EntityHubTemplate;
